import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";

import { fetchAssignmentListAPI } from "../worksheetAPI";
import { refinePages } from "../../../utils/paginationUtils";
import { formatDateString, showStudentURL } from "../../../utils/textUtils";
import secret from "../../../secret.json";

import DescriptionIcon from "@mui/icons-material/DescriptionOutlined";

import Pagination from "../../../components/Pagination";
import CustomCheckbox from "../../../components/CustomCheckbox";
import WorksheetSelectBar from "./common/WorksheetSelectBar";
import WorksheetTypeBox from "./common/WorksheetTypeBox";
import WorksheetSearchBar from "./common/WorksheetSearchBar";
import DeleteWorksheetModal from "./common/DeleteWorksheetModal";
import { CONTAINER_MAX_WIDTH } from "../../../styles/layout";

const AssignedRetryWorksheetTable = () => {
  const [isMobileW, setIsMobileW] = useState(Math.min(window.innerWidth, window.screen.width) <= 768);
  useEffect(() => {
    const f = () => setIsMobileW(Math.min(window.innerWidth, window.screen.width) <= 768);
    window.addEventListener("resize", f);
    window.addEventListener("orientationchange", f);
    return () => {
      window.removeEventListener("resize", f);
      window.removeEventListener("orientationchange", f);
    };
  }, []);

  const { user } = useSelector((state) => state.auth);
  
  const [results, setResults] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [typeSelect, setTypeSelect] = useState("all");
  const [keyword, setKeyword] = useState("");
  const [selectedWorksheets, setSelectedWorksheets] = useState([]);

  const visiblePages = useMemo(
    () => refinePages({ total_pages: totalPages, current_page: currentPage }),
    [currentPage, totalPages]
  );

  const fetchData = useCallback(async () => {
    try {
      const typeParam = typeSelect === "all" ? null : typeSelect;
      // 서버에서 복습 과제만 필터해서 받는다 (페이지 수/개수가 정확해짐)
      const response = await fetchAssignmentListAPI(
        currentPage,
        10,
        typeParam,
        null, // isFinished
        null, // levelId
        keyword,
        null, // studentId
        null, // studentGrade
        null, // userId
        true  // isRetry
      );

      setResults(response.results);
      setTotalPages(response.total_pages || 1);
    } catch (error) {
      console.error("Failed to fetch retry assignments:", error);
    }
  }, [currentPage, typeSelect, keyword]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePageClick = (page) => {
    setCurrentPage(page);
  };

  const handleCheckboxChange = (checked, worksheet) => {
    if (checked) {
      setSelectedWorksheets((prev) => [...prev, worksheet]);
    } else {
      setSelectedWorksheets((prev) =>
        prev.filter((prevWorksheet) => prevWorksheet.id !== worksheet.id)
      );
    }
  };

  const handleHeaderCheckboxChange = (checked) => {
    if (checked) {
      setSelectedWorksheets(results);
    } else {
      setSelectedWorksheets([]);
    }
  };

  const isHeaderChecked =
    results.length > 0 &&
    results.every((currentWorksheet) =>
      selectedWorksheets.some(
        (selectedWorksheet) => selectedWorksheet.id === currentWorksheet.id
      )
    );

  const handlePreviewClick = (assignment) => {
    const url = `${secret.STUDENT_BASE_URL}/preview?id=${assignment.id}&session=${user.session}`;
    window.open(url, "_blank");
  };

  return (
    <div
      style={{
        padding: "26px 22px 44px",
        maxWidth: `${CONTAINER_MAX_WIDTH}px`,
        margin: "0 auto",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: isMobileW ? "center" : "flex-end",
          gap: "8px",
          marginBottom: "18px",
          flexWrap: "nowrap",
        }}
      >
        {!isMobileW && (
          <h3 className="section-title">
            복습 문제지 목록
          </h3>
        )}
        <WorksheetSelectBar
          typeSelect={typeSelect}
          setTypeSelect={setTypeSelect}
        />
        <div
          style={{
            flex: isMobileW ? 1 : "none",
            width: isMobileW ? "auto" : "30%",
            minWidth: isMobileW ? 0 : "220px",
          }}
        >
          <WorksheetSearchBar
            placeholder="복습 문제지명 검색"
            handleFilter={setKeyword}
          />
        </div>
      </div>

      <div
        style={{
          backgroundColor: "#FFF",
          border: "1px solid #E6EDEC",
          borderRadius: "18px",
          overflow: "hidden",
          boxShadow: "0 10px 30px -22px rgba(20,40,50,.22)",
          paddingBottom: "5px",
          // [모바일 스와이프] 이 표만 처방 누락 — 고정 col 6개가 폰 폭에
          // 우겨넣어져 글자 세로 붕괴. 다른 표들과 동일: 좌우 스와이프.
          overflowX: Math.min(window.innerWidth, window.screen.width) <= 768 ? "auto" : "hidden",
          WebkitOverflowScrolling: "touch",
        }}
      >
        <table
          style={{
            width: "100%",
            minWidth: Math.min(window.innerWidth, window.screen.width) <= 768 ? "920px" : undefined,
            borderCollapse: "collapse",
            marginTop: "10px",
          }}
        >
          <colgroup>
            <col style={{ width: "48px" }} />
            <col style={{ width: "88px" }} />
            <col />
            <col style={{ width: "90px" }} />
            <col style={{ width: "74px" }} />
            <col style={{ width: "82px" }} />
            <col style={{ width: "92px" }} />
            <col style={{ width: "110px" }} />
          </colgroup>
          <tbody>
            <tr
              style={{
                textAlign: "left",
                borderBottom: "1.5px solid #0E141B",
                fontSize: "0.78rem",
                fontWeight: 700,
                color: "#7A8490",
              }}
            >
              <td style={{ textAlign: "center", paddingLeft: "6px" }}>
                <CustomCheckbox
                  checked={isHeaderChecked}
                  onChange={(e) => handleHeaderCheckboxChange(e.target.checked)}
                />
              </td>
              <td style={{ padding: Math.min(window.innerWidth, window.screen.width) <= 768 ? "9px 10px" : "13px 16px", whiteSpace: "nowrap" }}>문항 번호</td>
              <td style={{ padding: Math.min(window.innerWidth, window.screen.width) <= 768 ? "9px 10px" : "13px 16px", whiteSpace: "nowrap" }}>복습 문제지명</td>
              <td style={{ padding: Math.min(window.innerWidth, window.screen.width) <= 768 ? "9px 10px" : "13px 16px", textAlign: "center", whiteSpace: "nowrap" }}>학생명</td>
              <td style={{ padding: Math.min(window.innerWidth, window.screen.width) <= 768 ? "9px 10px" : "13px 16px", textAlign: "center", whiteSpace: "nowrap" }}>생성일</td>
              <td style={{ padding: Math.min(window.innerWidth, window.screen.width) <= 768 ? "9px 10px" : "13px 16px", textAlign: "center", whiteSpace: "nowrap" }}>마감일</td>
              <td style={{ padding: Math.min(window.innerWidth, window.screen.width) <= 768 ? "9px 10px" : "13px 16px", textAlign: "center", whiteSpace: "nowrap" }}>미리보기</td>
              <td style={{ padding: Math.min(window.innerWidth, window.screen.width) <= 768 ? "9px 10px" : "13px 16px", textAlign: "center", whiteSpace: "nowrap" }}>제출 시간</td>
            </tr>
            {results &&
              results.map((item) => {
                const isChecked = selectedWorksheets.includes(item);
                return (
                  <tr
                    key={item.id}
                    style={{
                      fontSize: "0.92rem",
                      borderBottom: "1px solid #E6EDEC",
                    }}
                  >
                    <td
                      style={{
                        textAlign: "center",
                        paddingLeft: "6px",
                      }}
                    >
                      <CustomCheckbox
                        checked={isChecked}
                        onChange={(e) =>
                          handleCheckboxChange(e.target.checked, item)
                        }
                      />
                    </td>
                    <td style={{ padding: Math.min(window.innerWidth, window.screen.width) <= 768 ? "9px 10px" : "13px 16px" }}>{item.id}</td>
                    <td
                      style={{
                        padding: Math.min(window.innerWidth, window.screen.width) <= 768 ? "9px 10px" : "13px 16px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>{item.title}</span>
                      <span style={{ flexShrink: 0 }}><WorksheetTypeBox type={item.type} title={item.title} /></span>
                      {item.is_retry && (
                        <span style={{
                          backgroundColor: "#E8F7EE",
                          color: "#1DB05B",
                          padding: "4px 10px",
                          borderRadius: "999px",
                          fontSize: "12px",
                          fontWeight: 800,
                          whiteSpace: "nowrap",
                          flexShrink: 0
                        }}>
                          복습 {item.retry_info?.retry_count || ''}회차
                        </span>
                      )}
                    </td>
                    <td style={{ padding: Math.min(window.innerWidth, window.screen.width) <= 768 ? "9px 10px" : "13px 16px", textAlign: "center", whiteSpace: "nowrap" }}>
                      {item.student?.name ? (
                        <a
                          href={showStudentURL(item.student.session)}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="학생 페이지 열기"
                          style={{
                            color: "#0E141B",
                            fontWeight: 700,
                            textDecoration: "none",
                            cursor: "pointer",
                            transition: "color .12s",
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.color = "#00A396")}
                          onMouseLeave={(e) => (e.currentTarget.style.color = "#0E141B")}
                        >
                          {item.student.name}
                        </a>
                      ) : (
                        ""
                      )}
                    </td>
                    <td style={{ padding: Math.min(window.innerWidth, window.screen.width) <= 768 ? "9px 10px" : "13px 16px", textAlign: "center" }}>
                      {formatDateString(item.created_at)}
                    </td>
                    <td style={{ padding: Math.min(window.innerWidth, window.screen.width) <= 768 ? "9px 10px" : "13px 16px", textAlign: "center" }}>
                      {formatDateString(item.closed_at)}
                    </td>
                    <td style={{ position: "relative" }}>
                      <button
                        onClick={() => handlePreviewClick(item)}
                        style={{
                          width: "34px",
                          height: "34px",
                          backgroundColor: "#FFF",
                          border: "1.5px solid #E6EDEC",
                          borderRadius: "10px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          color: "#3A434E",
                        }}
                        title="문서 열기"
                      >
                        <DescriptionIcon style={{ fontSize: "20px" }} />
                      </button>
                    </td>
                    <td
                      style={{
                        padding: Math.min(window.innerWidth, window.screen.width) <= 768 ? "9px 10px" : "13px 16px",
                        fontSize: "16px",
                        textAlign: "center",
                      }}
                    >
                      {item.finished_at ? formatDateString(item.finished_at) : "미제출"}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>

        <Pagination
          visiblePages={visiblePages}
          current_page={currentPage}
          handlePageClick={handlePageClick}
        />
      </div>

      {selectedWorksheets.length > 0 && (
        <DeleteWorksheetModal
          worksheets={selectedWorksheets}
          setSelectedWorksheets={setSelectedWorksheets}
          type={"retry"}
          onDeleted={fetchData}
        />
      )}
    </div>
  );
};

export default AssignedRetryWorksheetTable;
